import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { GA4Response, AnalyticsOverview, TrafficSource, DailyMetrics } from "@/types/analytics.types";

interface UseGA4AnalyticsOptions {
  startDate: string;
  endDate: string;
}

export const useGA4Analytics = ({ startDate, endDate }: UseGA4AnalyticsOptions) => {
  return useQuery({
    queryKey: ['ga4-analytics', startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('fetch-ga4-analytics', {
        body: {
          startDate,
          endDate,
          metrics: ['sessions', 'bounceRate', 'averageSessionDuration', 'conversions', 'eventCount'],
          dimensions: ['date', 'sessionDefaultChannelGroup'],
        },
      });

      if (error) throw error;
      
      const ga4Data = data as GA4Response;
      
      // Transform GA4 response into structured data
      return transformGA4Data(ga4Data);
    },
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });
};

function transformGA4Data(response: GA4Response) {
  const rows = response.rows || [];
  
  // Calculate overview metrics
  let totalSessions = 0;
  let totalBounces = 0;
  let totalDuration = 0;
  let totalConversions = 0;

  const trafficSourcesMap = new Map<string, {
    sessions: number;
    bounces: number;
    conversions: number;
  }>();

  const dailyMetricsMap = new Map<string, {
    sessions: number;
    bounces: number;
    conversions: number;
  }>();

  rows.forEach(row => {
    const date = row.dimensionValues.find(d => d.name === 'date')?.value || '';
    const channelGroup = row.dimensionValues.find(d => d.name === 'sessionDefaultChannelGroup')?.value || 'Unknown';
    
    const sessions = parseFloat(row.metricValues.find(m => m.name === 'sessions')?.value || '0');
    const bounceRate = parseFloat(row.metricValues.find(m => m.name === 'bounceRate')?.value || '0');
    const avgDuration = parseFloat(row.metricValues.find(m => m.name === 'averageSessionDuration')?.value || '0');
    const conversions = parseFloat(row.metricValues.find(m => m.name === 'conversions')?.value || '0');

    totalSessions += sessions;
    totalBounces += sessions * bounceRate;
    totalDuration += sessions * avgDuration;
    totalConversions += conversions;

    // Aggregate by traffic source
    const sourceData = trafficSourcesMap.get(channelGroup) || { sessions: 0, bounces: 0, conversions: 0 };
    sourceData.sessions += sessions;
    sourceData.bounces += sessions * bounceRate;
    sourceData.conversions += conversions;
    trafficSourcesMap.set(channelGroup, sourceData);

    // Aggregate by date
    const dailyData = dailyMetricsMap.get(date) || { sessions: 0, bounces: 0, conversions: 0 };
    dailyData.sessions += sessions;
    dailyData.bounces += sessions * bounceRate;
    dailyData.conversions += conversions;
    dailyMetricsMap.set(date, dailyData);
  });

  const overview: AnalyticsOverview = {
    bounceRate: totalSessions > 0 ? (totalBounces / totalSessions) * 100 : 0,
    avgSessionDuration: totalSessions > 0 ? totalDuration / totalSessions : 0,
    totalSessions,
    totalConversions,
    conversionRate: totalSessions > 0 ? (totalConversions / totalSessions) * 100 : 0,
  };

  const trafficSources: TrafficSource[] = Array.from(trafficSourcesMap.entries()).map(([source, data]) => ({
    source,
    sessions: data.sessions,
    bounceRate: data.sessions > 0 ? (data.bounces / data.sessions) * 100 : 0,
    conversions: data.conversions,
  }));

  const dailyMetrics: DailyMetrics[] = Array.from(dailyMetricsMap.entries())
    .map(([date, data]) => ({
      date,
      sessions: data.sessions,
      bounceRate: data.sessions > 0 ? (data.bounces / data.sessions) * 100 : 0,
      conversions: data.conversions,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    overview,
    trafficSources,
    dailyMetrics,
  };
}
